import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMagnifyingGlass, faSliders } from '@fortawesome/pro-solid-svg-icons';
import { type RequestToken } from '../../types';
import { type HandlerAppInfo } from '../../data/handlerApps';
import { useTranslation } from '../../i18n/i18n';
import { useAppPicker } from '../../hooks/useAppPicker';
import CountryFilterModal from '../../components/CountryFilterModal';
import AppTile from '../../components/AppTile';

type Props = {
  tokens: RequestToken[];
  handlerApps: Record<string, HandlerAppInfo>;
  recommendedHandlerAppUuids: string[];
  onSelect: (token: RequestToken) => void;
  className?: string;
};

export default function AppPickerContent({
  tokens,
  handlerApps,
  recommendedHandlerAppUuids,
  onSelect,
  className,
}: Props) {
  const { t } = useTranslation();
  const picker = useAppPicker(
    tokens,
    handlerApps,
    recommendedHandlerAppUuids,
    onSelect,
  );

  return (
    <div
      className={`flex flex-col items-center gap-6 w-full ${className ?? ''}`}
    >
      <div className="flex gap-2 w-full max-w-lg">
        <div className="flex flex-1 items-center gap-2 rounded-md border border-input bg-card px-3 py-1 shadow-xs h-9">
          <FontAwesomeIcon
            icon={faMagnifyingGlass}
            className="size-4 shrink-0 text-muted-foreground"
          />
          <input
            type="text"
            value={picker.search}
            onChange={(e) => picker.setSearch(e.target.value)}
            placeholder={t('appPicker.searchPlaceholder')}
            className="flex-1 bg-transparent text-sm text-foreground placeholder:text-muted-foreground outline-none"
          />
        </div>
        <button
          type="button"
          onClick={() => picker.setFilterOpen(true)}
          className="relative flex items-center justify-center size-9 shrink-0 rounded-md border border-input bg-card shadow-xs hover:bg-accent transition-colors"
          aria-label={t('appPicker.filterTitle')}
        >
          <FontAwesomeIcon
            icon={faSliders}
            className="size-4 text-foreground"
          />
          {picker.activeFilterCount > 0 && (
            <span className="absolute -top-1 -right-1 flex items-center justify-center size-4 rounded-full bg-foreground text-background text-[10px] font-medium">
              {picker.activeFilterCount}
            </span>
          )}
        </button>
      </div>

      <div className="flex flex-1 flex-col gap-6 w-full max-w-lg min-h-0 overflow-y-auto text-left py-1">
        {picker.hasResults ? (
          <>
            {picker.lastUsed && (
              <div className="flex flex-col gap-2">
                <p className="text-xs font-medium text-muted-foreground">
                  {t('appPicker.sectionLastUsed')}
                </p>
                <AppTile
                  app={picker.lastUsed.app}
                  fallbackName={picker.lastUsed.name}
                  onClick={() => picker.handleSelect(picker.lastUsed!.token)}
                />
              </div>
            )}
            {picker.recommended.length > 0 && (
              <div className="flex flex-col gap-2">
                <p className="text-xs font-medium text-muted-foreground">
                  {t('appPicker.sectionRecommended')}
                </p>
                {picker.recommended.map((item, i) => (
                  <AppTile
                    key={item.token.payload.body.handlerAppUuid ?? `rec-${i}`}
                    app={item.app}
                    fallbackName={item.name}
                    onClick={() => picker.handleSelect(item.token)}
                  />
                ))}
              </div>
            )}
            {picker.az.length > 0 && (
              <div className="flex flex-col gap-2">
                <p className="text-xs font-medium text-muted-foreground">
                  {t('appPicker.sectionAZ')}
                </p>
                {picker.az.map((item, i) => (
                  <AppTile
                    key={item.token.payload.body.handlerAppUuid ?? `az-${i}`}
                    app={item.app}
                    fallbackName={item.name}
                    onClick={() => picker.handleSelect(item.token)}
                  />
                ))}
              </div>
            )}
          </>
        ) : (
          <p className="text-sm text-muted-foreground text-center py-4">
            {t('appPicker.noResults')}
          </p>
        )}
      </div>

      <CountryFilterModal
        open={picker.filterOpen}
        selected={picker.countryFilter}
        onChange={picker.setCountryFilter}
        onClose={() => picker.setFilterOpen(false)}
      />
    </div>
  );
}
